import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import AspApi from 'api/ApiConfig';
import {
  listDataSources,
  showDataSource,
  createDataSource,
  deleteDataSource,
  usageMetrics,
  showLearningDocument,
} from './services/corpus.service';
import { crmTeamMembers } from '../modules/crm/api/Api';

const normalizeEntity = (payload) => {
  if (!payload) return null;
  if (Array.isArray(payload)) return payload;
  if (payload?.data) return payload.data;
  return payload;
};

const removeId = (collection, id) =>
  collection.filter((itemId) => String(itemId) !== String(id));

const initialState = {
  learningDocument: 0,
  dataSources: [],
  meta: null,
  loading: false,
  error: null,
  currentDataSource: null,
  currentLoading: false,
  currentError: null,
  creating: false,
  usageMetrics: {},
  updatingIds: [],
  deletingIds: [],
  syncingIds: [],

  // for chatbot onboarding flow

  shouldShowConfigSteps: true,
  isChatbotConfigured: false,
  agentConfiguration: null,
  agentConfigLoading: false,
  agentConfigSaving: false,
  agentConfigError: null,
  agentConfigSavingError: null,

  crmTeamMembers: [],
  crmTeamMembersLoading: false,
  crmTeamMembersError: null,
};

export const fetchAgentConfiguration = createAsyncThunk(
  'corpus/fetchAgentConfiguration',
  async ({ brandId }, { rejectWithValue }) => {
    try {
      const res = await AspApi.get(
        `/api/v1/svc/ai_agents/agents/${brandId}?brand_id=${brandId}`,
      );
      return res?.data?.data || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || error);
    }
  },
);

export const createAgentConfiguration = createAsyncThunk(
  'corpus/createAgentConfiguration',
  async ({ brandId, whatsappNumberId, configuration }, { rejectWithValue }) => {
    try {
      const res = await AspApi.post(
        `/api/v1/svc/ai_agents/agents?brand_id=${brandId}`,
        {
          brand_id: brandId,
          whatsapp_number_id: whatsappNumberId,
          configuration,
        },
      );
      return res?.data?.data || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || error);
    }
  },
);

export const updateAgentConfiguration = createAsyncThunk(
  'corpus/updateAgentConfiguration',
  async ({ brandId, configuration }, { rejectWithValue }) => {
    try {
      const res = await AspApi.put(
        `/api/v1/svc/ai_agents/agents/${brandId}?brand_id=${brandId}`,
        {
          brand_id: brandId,
          configuration,
        },
      );
      return res?.data?.data || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || error);
    }
  },
);

export const fetchCrmTeamMembers = createAsyncThunk(
  'corpus/fetchCrmTeamMembers',
  async ({ brandId }, { rejectWithValue }) => {
    try {
      const res = await crmTeamMembers(brandId);
      // console.log({res});
      return res?.data?.users || res?.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message || error);
    }
  },
);

export const corpusSlice = createSlice({
  name: 'corpus',
  initialState,
  reducers: {
    setShouldShowConfigSteps: (state, action) => {
      state.shouldShowConfigSteps = Boolean(action.payload);
    },
    setCurrentDataSource: (state, action) => {
      const id = action.payload;
      state.currentDataSource =
        state.dataSources.find(
          (item) => String(item?.uuid ?? item?.id) === String(id),
        ) || null;
    },
    clearCurrentDataSource: (state) => {
      state.currentDataSource = null;
      state.currentError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAgentConfiguration.pending, (state) => {
        state.agentConfigLoading = true;
        state.agentConfigError = null;
      })
      .addCase(fetchAgentConfiguration.fulfilled, (state, action) => {
        state.agentConfigLoading = false;
        state.agentConfiguration = action.payload?.data || action.payload;
      })
      .addCase(fetchAgentConfiguration.rejected, (state, action) => {
        state.agentConfigLoading = false;
        state.agentConfigError =
          action.payload || action.error?.message || null;
      })
      .addCase(createAgentConfiguration.pending, (state) => {
        state.agentConfigSaving = true;
        state.agentConfigSavingError = null;
      })
      .addCase(createAgentConfiguration.fulfilled, (state, action) => {
        state.agentConfigSaving = false;
        state.agentConfiguration = action.payload?.data || action.payload;
        state.isChatbotConfigured = true;
      })
      .addCase(createAgentConfiguration.rejected, (state, action) => {
        state.agentConfigSaving = false;
        state.agentConfigSavingError =
          action.payload || action.error?.message || null;
      })
      .addCase(updateAgentConfiguration.pending, (state) => {
        state.agentConfigSaving = true;
        state.agentConfigSavingError = null;
      })
      .addCase(updateAgentConfiguration.fulfilled, (state, action) => {
        state.agentConfigSaving = false;
        state.agentConfiguration = action.payload?.data || action.payload;
      })
      .addCase(updateAgentConfiguration.rejected, (state, action) => {
        state.agentConfigSaving = false;
        state.agentConfigSavingError =
          action.payload || action.error?.message || null;
      })
      .addCase(listDataSources.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listDataSources.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        const list = normalizeEntity(payload);
        state.dataSources = Array.isArray(list) ? list : [];
        state.meta = {
          ...(state.meta || {}),
          count:
            payload?.count ??
            payload?.meta?.count ??
            payload?.total ??
            state.dataSources.length,
        };
        state.usageMetrics = { ...payload?.usage_metrics };
        state.learningDocument =
          payload?.learning_document?.learning_document || 0;
      })
      .addCase(listDataSources.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message || null;
      })
      .addCase(showDataSource.pending, (state, action) => {
        const id = action.meta?.arg?.id;
        if (id) {
          state.syncingIds = [...state.syncingIds, id];
        }
        state.currentLoading = true;
        state.currentError = null;
      })
      .addCase(showDataSource.fulfilled, (state, action) => {
        state.currentLoading = false;
        const entities = normalizeEntity(action.payload);
        const entity = Array.isArray(entities) ? entities[0] : entities;
        const argId = action.meta?.arg?.id;
        const resolvedId = entity?.uuid || entity?.id || argId;
        state.syncingIds = removeId(state.syncingIds, argId);
        if (entity) {
          state.currentDataSource = entity;
          if (resolvedId) {
            const index = state.dataSources.findIndex(
              (item) => String(item?.uuid ?? item?.id) === String(resolvedId),
            );
            if (index >= 0) {
              state.dataSources[index] = {
                ...state.dataSources[index],
                ...entity,
              };
            } else {
              state.dataSources = [entity, ...state.dataSources];
            }
          }
        }
        if (action?.payload?.usage_metrics) {
          state.usageMetrics = { ...action?.payload?.usage_metrics };
        }
      })
      .addCase(showDataSource.rejected, (state, action) => {
        state.currentLoading = false;
        state.currentError = action.payload || action.error?.message || null;
        state.syncingIds = removeId(state.syncingIds, action.meta?.arg?.id);
      })
      .addCase(createDataSource.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createDataSource.fulfilled, (state, action) => {
        state.creating = false;
        state.learningDocument = state.learningDocument + 1;
        const entities = normalizeEntity(action.payload);
        const list = (Array.isArray(entities) ? entities : [entities]).filter(
          Boolean,
        );

        if (list.length) {
          const existingIds = new Set(
            state.dataSources.map((item) => String(item?.uuid ?? item?.id)),
          );
          const createdIds = new Set(
            list.map((item) => String(item?.uuid ?? item?.id)).filter(Boolean),
          );
          const newUniqueCount = list.reduce((count, item) => {
            const id = String(item?.uuid ?? item?.id);
            if (!id) return count;
            return existingIds.has(id) ? count : count + 1;
          }, 0);

          state.dataSources = [
            ...list,
            ...state.dataSources.filter(
              (item) => !createdIds.has(String(item?.uuid ?? item?.id)),
            ),
          ];

          const nextMetaCount = (() => {
            const currentCount = Number.isFinite(state.meta?.count)
              ? Number(state.meta.count)
              : null;
            if (currentCount !== null) {
              return currentCount + newUniqueCount;
            }
            return state.dataSources.length;
          })();

          state.meta = {
            ...(state.meta || {}),
            count: nextMetaCount,
          };

          state.currentDataSource = list[0];
        }
        if (action?.payload?.usage_metrics) {
          state.usageMetrics = { ...action?.payload?.usage_metrics };
        }
      })
      .addCase(createDataSource.rejected, (state, action) => {
        state.creating = false;
        // state.error = action.payload || action.error?.message || null;
      })
      .addCase(deleteDataSource.pending, (state, action) => {
        const id = action.meta?.arg?.id;
        if (id) {
          state.deletingIds = [...state.deletingIds, id];
        }
        state.error = null;
      })
      .addCase(deleteDataSource.fulfilled, (state, action) => {
        const id = action.meta?.arg?.id ?? action.payload;
        state.deletingIds = removeId(state.deletingIds, id);
        state.dataSources = state.dataSources.filter(
          (item) => String(item?.uuid ?? item?.id) !== String(id),
        );
        if (
          state.currentDataSource &&
          String(
            state.currentDataSource?.uuid ?? state.currentDataSource?.id,
          ) === String(id)
        ) {
          state.currentDataSource = null;
        }
        if (action?.payload?.usage_metrics) {
          state.usageMetrics = { ...action?.payload?.usage_metrics };
        }
      })
      .addCase(deleteDataSource.rejected, (state, action) => {
        state.deletingIds = removeId(state.deletingIds, action.payload);
      })
      .addCase(usageMetrics.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(usageMetrics.fulfilled, (state, action) => {
        state.loading = false;
        state.usageMetrics = action.payload || {};
      })
      .addCase(usageMetrics.rejected, (state, action) => {
        state.loading = false;
        state.usageMetrics = {};
        state.error = action.payload || action.error?.message || null;
      })
      .addCase(showLearningDocument.pending, (state) => {
        state.error = null;
      })
      .addCase(showLearningDocument.fulfilled, (state, action) => {
        state.learningDocument = action.payload?.learning_document || 0;
      })
      .addCase(showLearningDocument.rejected, (state, action) => {
        state.error = action.payload || 'Failed to fetch learning document';
      })
      .addCase(fetchCrmTeamMembers.pending, (state) => {
        state.crmTeamMembersLoading = true;
        state.crmTeamMembersError = null;
      })
      .addCase(fetchCrmTeamMembers.fulfilled, (state, action) => {
        state.crmTeamMembersLoading = false;
        state.crmTeamMembers = action.payload || [];
      })
      .addCase(fetchCrmTeamMembers.rejected, (state, action) => {
        state.crmTeamMembersLoading = false;
        state.crmTeamMembersError =
          action.payload || action.error?.message || null;
      });
  },
});

export const {
  setCurrentDataSource,
  clearCurrentDataSource,
  setShouldShowConfigSteps,
} = corpusSlice.actions;

export const selectCorpusState = (state) => state?.corpus;
export const selectCorpusLoading = (state) => selectCorpusState(state)?.loading;
export const selectCorpusError = (state) => selectCorpusState(state)?.error;
export const selectDataSources = (state) =>
  selectCorpusState(state)?.dataSources || [];

export const selectLearningDocument = (state) =>
  selectCorpusState(state)?.learningDocument || 0;
export const selectCurrentDataSource = (state) =>
  selectCorpusState(state)?.currentDataSource;
export const selectCorpusMeta = (state) => selectCorpusState(state)?.meta;
export const selectCorpusCreating = (state) =>
  selectCorpusState(state)?.creating;
export const selectCorpusDeletingIds = (state) =>
  selectCorpusState(state)?.deletingIds || [];
export const selectCorpusUpdatingIds = (state) =>
  selectCorpusState(state)?.updatingIds || [];
export const selectCorpusSyncingIds = (state) =>
  selectCorpusState(state)?.syncingIds || [];
export const selectCorpusUsageMetrics = (state) =>
  selectCorpusState(state)?.usageMetrics;
export const selectShouldShowConfigSteps = (state) =>
  selectCorpusState(state)?.shouldShowConfigSteps;
export const selectAgentConfiguration = (state) =>
  selectCorpusState(state)?.agentConfiguration;
export const selectAgentConfigLoading = (state) =>
  selectCorpusState(state)?.agentConfigLoading;
export const selectAgentConfigSaving = (state) =>
  selectCorpusState(state)?.agentConfigSaving;
export const selectIsChatbotConfigured = (state) =>
  selectCorpusState(state)?.isChatbotConfigured;

export const selectCrmTeamMembers = (state) =>
  selectCorpusState(state)?.crmTeamMembers || [];
export const selectCrmTeamMembersLoading = (state) =>
  selectCorpusState(state)?.crmTeamMembersLoading;

export default corpusSlice.reducer;
